/**
 * The rows of "Pick up where you left off" (ADR-0070), gathered from the lists
 * each page already uses. A source the person cannot reach is not asked for,
 * and one that fails leaves the others standing.
 */

import {
  IconClipboardCheck,
  IconMessageCircle,
  IconRoute,
  IconShieldSearch,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { STATUS_LABEL } from "@/features/validation/stageMeta";
import { listAssessments, listChats } from "@/shared/api/ai";
import { listRecentRuns } from "@/shared/api/projects";
import { listRuns } from "@/shared/api/validation";

const SHOWN = 6;

export interface RecentItem {
  id: string;
  title: string;
  /** What it is and where it stands: "Release validation run · Executing". */
  what: string;
  at: string | null;
  to: string;
  icon: ReactNode;
  color: string;
}

/** Which chats each agent's surface holds, and where one opens. */
const CHAT_SURFACES: {
  agent: string;
  surface: "explore" | "support" | "user_admin";
  label: string;
  path: string;
}[] = [
  { agent: "explore", surface: "explore", label: "Explore", path: "/solutions/explore" },
  {
    agent: "first_line_support",
    surface: "support",
    label: "1st line support",
    path: "/solutions/validation/first-line-support",
  },
  {
    agent: "second_line_support",
    surface: "user_admin",
    label: "2nd line support",
    path: "/solutions/validation/second-line-support",
  },
];

export interface Person {
  user_id: string;
  agents: string[];
  roles: string[];
}

/** Every source the person can reach, merged newest first. Exported for tests. */
export async function loadRecent(token: string, me: Person): Promise<RecentItem[]> {
  const has = (agent: string) => me.agents.includes(agent);
  const builder = me.roles.includes("developer") || me.roles.includes("administrator");
  const sources: Promise<RecentItem[]>[] = [];

  if (has("release_validation")) {
    sources.push(
      listRuns(token).then((out) =>
        out.items
          .filter((r) => r.is_owner || r.my_role)
          .map((r) => ({
            id: `run:${r.run_id}`,
            title: r.title,
            what: `Release validation run · ${STATUS_LABEL[r.status] ?? r.status}`,
            at: r.updated_at_utc,
            to: `/validation/runs/${encodeURIComponent(r.run_id)}`,
            icon: <IconClipboardCheck size={18} />,
            color: "blue",
          })),
      ),
      listAssessments(token).then((out) =>
        out.items
          .filter((a) => a.owner_user_id === me.user_id)
          .map((a) => ({
            id: `assessment:${a.assessment_id}`,
            title: a.title,
            what: `Risk assessment · ${a.status}`,
            at: a.created_at_utc,
            to: `/validation/risk-assessment?assessment=${encodeURIComponent(a.assessment_id)}`,
            icon: <IconShieldSearch size={18} />,
            color: "violet",
          })),
      ),
    );
  }
  for (const s of CHAT_SURFACES.filter((c) => has(c.agent))) {
    sources.push(
      listChats(token, s.surface).then((out) =>
        out.items.map((c) => ({
          id: `chat:${c.chat_id}`,
          title: c.name,
          what: `Chat · ${s.label}`,
          at: c.updated_at_utc,
          to: `${s.path}/${encodeURIComponent(c.chat_id)}`,
          icon: <IconMessageCircle size={18} />,
          color: "cyan",
        })),
      ),
    );
  }
  if (builder) {
    sources.push(
      listRecentRuns(token).then((out) => {
        // One row per pipeline: its latest run (the feed is newest first).
        const seen = new Set<string>();
        const rows: RecentItem[] = [];
        for (const r of out.items) {
          if (!r.project_id || seen.has(r.pipeline_id)) continue;
          seen.add(r.pipeline_id);
          rows.push({
            id: `pipeline:${r.pipeline_id}`,
            title: r.pipeline_name ?? "Pipeline",
            what: `Pipeline · last run ${r.status}`,
            at: r.ended_at_utc,
            to: `/projects/${encodeURIComponent(r.project_id)}/pipelines/${encodeURIComponent(r.pipeline_id)}`,
            icon: <IconRoute size={18} />,
            color: "orange",
          });
        }
        return rows;
      }),
    );
  }

  const settled = await Promise.allSettled(sources);
  const items = settled.flatMap((s) => (s.status === "fulfilled" ? s.value : []));
  return items.sort((a, b) => (b.at ?? "").localeCompare(a.at ?? "")).slice(0, SHOWN);
}
